import { TextFieldController } from '@components/controllers/TextFieldController';
import { FormValues } from '@features/createDevice/validators';
import { RemoveCircleOutline } from '@mui/icons-material';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { FC, Fragment } from 'react';
import { Control, useFieldArray, useWatch } from 'react-hook-form';
import { getAllowedChildren, getSensorLabel, getSensorTypeLabel } from './deviceTypes';

interface Props {
  control: Control<FormValues>;
}

const getNextLocalSensorId = (sensors: FormValues['children']) => {
  if (!sensors) return 1;
  let id = 1;
  for (const sensor of sensors) {
    for (const mapper of sensor.sensorsMappers) {
      const sensorId = mapper.telemetrySensorId;
      if (sensorId) {
        id = Math.max(id, sensorId) + 1;
      }
    }
  }
  return id;
};

export const SensorsController: FC<Props> = ({ control }) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'children',
  });

  const sensors = useWatch({
    control,
    name: 'children',
    defaultValue: [],
  });

  const type = useWatch({
    control,
    name: 'type',
  });

  const allowedChildren = getAllowedChildren(type);

  return (
    <Box display="grid" gap={2}>
      <Box display="grid" alignItems="center" gridTemplateColumns="auto 1fr" width="100%">
        <Typography>Sensores</Typography>
        <Box justifyContent="end" display="flex" gap={1}>
          {allowedChildren.map((child) => {
            const sensorLabel = getSensorLabel(child.sensorName, child.sensorVersion);
            const mappers = child.mappers;

            return (
              <Button
                key={sensorLabel}
                onClick={() => {
                  const nextId = getNextLocalSensorId(sensors);

                  append({
                    sensorName: child.sensorName,
                    sensorVersion: child.sensorVersion,
                    sensorsMappers: mappers.map((mapper, index) => ({
                      typeId: mapper[0],
                      telemetrySensorId: nextId + index,
                    })),
                  });
                }}
              >
                Agregar T-{sensorLabel}
              </Button>
            );
          })}
        </Box>
      </Box>

      {fields.length === 0 && (
        <Typography color="text.secondary" fontStyle="italic">
          Agrega los sensores necesarios
        </Typography>
      )}
      {fields.map((field, index) => {
        const sensorLabel = getSensorLabel(field.sensorName, field.sensorVersion);
        return (
          <Box
            display="grid"
            gridTemplateColumns="2fr 3fr"
            gap={2}
            alignItems="start"
            key={field.id}
          >
            <Box display="flex" gap={1} alignItems="center" pt="1px">
              <IconButton onClick={() => remove(index)} color="error" size="small">
                <RemoveCircleOutline fontSize="small" />
              </IconButton>
              <Typography fontWeight="bold">T-{sensorLabel}</Typography>
            </Box>
            <Box display="grid" gridTemplateColumns="2fr 1fr" alignItems="center">
              {field.sensorsMappers.map((mapper, mapperIndex) => {
                const sensorTypeLabel = getSensorTypeLabel(mapper.typeId);
                return (
                  <Fragment key={mapperIndex}>
                    <Typography fontSize={15}>ID {sensorTypeLabel}</Typography>
                    <TextFieldController
                      name={`children.${index}.sensorsMappers.${mapperIndex}.telemetrySensorId`}
                      control={control}
                      slotProps={{
                        textField: {
                          variant: 'standard',
                          size: 'small',
                        },
                      }}
                    />
                  </Fragment>
                );
              })}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};
